import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cloud, Users, LogOut, Shield, Database, RefreshCw, ExternalLink, MessageSquare, TrendingUp, BookOpen } from 'lucide-react';
import BlogEditor from './admin/BlogEditor';
import axios from 'axios';

interface AdminUser {
  id: number;
  email: string;
  created_at: string;
  is_admin: boolean;
  aws_accounts_count: number;
}

interface AdminData {
  totalUsers: number;
  users: AdminUser[];
}

interface FeedItem {
  id: string;
  title: string;
  url: string;
  source: 'hn' | 'reddit' | 'devto' | 'blogs' | 'amazon';
  author: string;
  points?: number;
  comments?: number;
  publishedAt: string;
}

type FeedSource = 'all' | 'hn' | 'reddit' | 'devto' | 'blogs' | 'amazon';

const SOURCE_META: Record<FeedSource, { label: string; color: string }> = {
  all:    { label: 'All',       color: 'bg-gray-100 text-gray-700' },
  amazon: { label: 'AWS News',  color: 'bg-amber-100 text-amber-800' },
  hn:     { label: 'HN',        color: 'bg-orange-100 text-orange-700' },
  reddit: { label: 'Reddit',    color: 'bg-red-100 text-red-700' },
  devto:  { label: 'Dev.to',    color: 'bg-purple-100 text-purple-700' },
  blogs:  { label: 'AWS Blog',  color: 'bg-blue-100 text-blue-700' },
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

async function fetchHN(): Promise<FeedItem[]> {
  const queries = ['cloud cost optimization', 'finops', 'aws cost'];
  const results = await Promise.all(queries.map(q =>
    fetch(`https://hn.algolia.com/api/v1/search?query=${encodeURIComponent(q)}&tags=story&hitsPerPage=8`)
      .then(r => r.json())
  ));
  const seen = new Set<string>();
  return results.flatMap(r => r.hits || [])
    .filter((h: any) => {
      if (seen.has(h.objectID)) return false;
      seen.add(h.objectID);
      return h.title && h.points > 5;
    })
    .sort((a: any, b: any) => b.points - a.points)
    .slice(0, 15)
    .map((h: any) => ({
      id: `hn-${h.objectID}`,
      title: h.title,
      url: h.url || `https://news.ycombinator.com/item?id=${h.objectID}`,
      source: 'hn',
      author: h.author,
      points: h.points,
      comments: h.num_comments,
      publishedAt: h.created_at,
    }));
}

async function fetchReddit(): Promise<FeedItem[]> {
  const res = await fetch(
    'https://www.reddit.com/r/aws+devops+finops+cloudcomputing/search.json?q=cloud+cost+optimization&sort=new&limit=20&t=month',
    { headers: { 'Accept': 'application/json' } }
  );
  const json = await res.json();
  return (json.data?.children || [])
    .filter((c: any) => c.data.score > 2)
    .slice(0, 15)
    .map((c: any) => ({
      id: `reddit-${c.data.id}`,
      title: c.data.title,
      url: `https://reddit.com${c.data.permalink}`,
      source: 'reddit',
      author: c.data.author,
      points: c.data.score,
      comments: c.data.num_comments,
      publishedAt: new Date(c.data.created_utc * 1000).toISOString(),
    }));
}

async function fetchDevTo(): Promise<FeedItem[]> {
  const tags = ['finops', 'aws', 'cloudcost'];
  const results = await Promise.all(tags.map(tag =>
    fetch(`https://dev.to/api/articles?tag=${tag}&per_page=8&top=7`).then(r => r.json())
  ));
  const seen = new Set<string>();
  return results.flat()
    .filter((a: any) => {
      if (seen.has(String(a.id))) return false;
      seen.add(String(a.id));
      return a.title;
    })
    .sort((a: any, b: any) => (b.positive_reactions_count || 0) - (a.positive_reactions_count || 0))
    .slice(0, 15)
    .map((a: any) => ({
      id: `devto-${a.id}`,
      title: a.title,
      url: a.url,
      source: 'devto',
      author: a.user?.username || 'unknown',
      points: a.positive_reactions_count,
      comments: a.comments_count,
      publishedAt: a.published_at,
    }));
}

const API = import.meta.env.VITE_API_BASE_URL;

async function fetchRSS(rssUrl: string): Promise<any[]> {
  const res = await fetch(`${API}/api/rss?url=${encodeURIComponent(rssUrl)}`);
  const json = await res.json();
  return json.items || [];
}

async function fetchBlogs(): Promise<FeedItem[]> {
  const feeds = [
    'https://aws.amazon.com/blogs/aws/feed/',
    'https://aws.amazon.com/blogs/compute/feed/',
    'https://aws.amazon.com/blogs/architecture/feed/',
  ];
  const results = await Promise.all(feeds.map(rss => fetchRSS(rss)));
  const seen = new Set<string>();
  return results.flat()
    .filter((item: any) => {
      if (seen.has(item.link)) return false;
      seen.add(item.link);
      return item.title;
    })
    .sort((a: any, b: any) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime())
    .slice(0, 15)
    .map((item: any) => ({
      id: `blog-${encodeURIComponent(item.link)}`,
      title: item.title,
      url: item.link,
      source: 'blogs',
      author: item.author || 'AWS',
      publishedAt: item.pubDate,
    }));
}

async function fetchAmazonNews(): Promise<FeedItem[]> {
  // Combine multiple AWS sources for a rich FinOps / optimization feed
  const [aboutAmazon, finOpsBlog, whatsNew] = await Promise.allSettled([
    // 1. aboutamazon.com/news/aws — corporate AWS stories
    fetch(`${API}/api/news/aws`).then(r => r.json()).then(j => j.items || []),
    // 2. AWS Cloud Financial Management blog — pure FinOps
    fetchRSS('https://aws.amazon.com/blogs/aws-cloud-financial-management/feed/'),
    // 3. AWS What's New — product launches, pricing changes
    fetchRSS('https://aws.amazon.com/about-aws/whats-new/recent/feed/'),
  ]);

  const items: FeedItem[] = [];
  const seen = new Set<string>();
  const add = (title: string, url: string, author: string, date?: string) => {
    if (seen.has(url) || !title || title.length < 10) return;
    seen.add(url);
    items.push({ id: `amazon-${seen.size}`, title, url, source: 'amazon', author, publishedAt: date || new Date().toISOString() });
  };

  // aboutamazon articles
  if (aboutAmazon.status === 'fulfilled') {
    (aboutAmazon.value as any[]).forEach((a: any) => add(a.title, a.url, 'Amazon'));
  }

  // FinOps blog — all articles are relevant
  if (finOpsBlog.status === 'fulfilled') {
    (finOpsBlog.value as any[]).slice(0, 15).forEach((a: any) => add(a.title, a.link, a.author || 'AWS', a.pubDate));
  }

  // What's New — filter to cost/optimization/pricing relevant items
  const costKeywords = /cost|pric|sav|optim|reserv|spot|budget|billing|financial|finops|free tier|discount|rightsiz|graviton|compute optimizer/i;
  if (whatsNew.status === 'fulfilled') {
    (whatsNew.value as any[])
      .filter((a: any) => costKeywords.test(`${a.title} ${a.description || ''} ${a.category || ''}`))
      .slice(0, 10)
      .forEach((a: any) => add(a.title, a.link, 'AWS', a.pubDate));
  }

  // Sort by date (newest first), aboutamazon items without dates will cluster at top
  items.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
  return items.slice(0, 25);
}

export default function AdminPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<AdminData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [activeTab, setActiveTab] = useState<'metrics' | 'news' | 'blog'>('metrics');
  const [feeds, setFeeds] = useState<FeedItem[]>([]);
  const [feedsLoading, setFeedsLoading] = useState(true);
  const [activeSource, setActiveSource] = useState<FeedSource>('all');

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      try {
        const res = await axios.get(
          `${import.meta.env.VITE_API_BASE_URL}/api/admin/users`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setData(res.data);
      } catch (err: any) {
        if (err.response?.status === 401) { localStorage.removeItem('token'); navigate('/login'); }
        else if (err.response?.status === 403) { navigate('/dashboard'); }
        else { setError('Failed to load admin data'); }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [navigate]);

  const fetchFeeds = useCallback(async () => {
    setFeedsLoading(true);
    const results = await Promise.allSettled([fetchHN(), fetchReddit(), fetchDevTo(), fetchBlogs(), fetchAmazonNews()]);
    const all: FeedItem[] = results
      .filter(r => r.status === 'fulfilled')
      .flatMap(r => (r as PromiseFulfilledResult<FeedItem[]>).value);
    all.sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime());
    setFeeds(all);
    setFeedsLoading(false);
  }, []);

  useEffect(() => { fetchFeeds(); }, [fetchFeeds]);

  const handleLogout = () => { localStorage.removeItem('token'); navigate('/'); };

  const filteredFeeds = activeSource === 'all' ? feeds : feeds.filter(f => f.source === activeSource);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
            <Cloud className="w-6 h-6 text-emerald-800 animate-spin" />
          </div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Nav */}
      <nav className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-[#0F3D2E] rounded-lg flex items-center justify-center">
            <Cloud className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-gray-900">CloudFrugal</span>
          <span className="text-xs font-semibold px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
            <Shield className="w-3 h-3" /> Admin
          </span>
        </div>
        <button onClick={handleLogout} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors">
          <LogOut className="w-4 h-4" /> Logout
        </button>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
          <p className="text-sm text-gray-500 mt-1">Internal use only — CloudFrugal team</p>
        </div>

        {/* Soft tabs */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('metrics')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'metrics'
                ? 'bg-[#0F3D2E] text-white shadow-md shadow-blue-200'
                : 'bg-white text-gray-500 border border-gray-200 hover:border-blue-300 hover:text-emerald-800'
            }`}
          >
            <Database className="w-4 h-4" />
            Metrics
          </button>
          <button
            onClick={() => setActiveTab('news')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'news'
                ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-md shadow-purple-200'
                : 'bg-white text-gray-500 border border-gray-200 hover:border-purple-300 hover:text-purple-600'
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Community News
          </button>
          <button
            onClick={() => setActiveTab('blog')}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
              activeTab === 'blog'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-md shadow-green-200'
                : 'bg-white text-gray-500 border border-gray-200 hover:border-green-300 hover:text-green-600'
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Blog
          </button>
        </div>

        {/* Metrics tab */}
        {activeTab === 'metrics' && <div className="space-y-8">

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#e8f0ec] flex items-center justify-center">
              <Users className="w-6 h-6 text-emerald-800" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">{data?.totalUsers}</p>
              <p className="text-sm text-gray-500">Total Users</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
              <Database className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {data?.users.reduce((sum, u) => sum + u.aws_accounts_count, 0)}
              </p>
              <p className="text-sm text-gray-500">AWS Accounts Connected</p>
            </div>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center">
              <Shield className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">
                {data?.users.filter(u => u.aws_accounts_count > 0).length}
              </p>
              <p className="text-sm text-gray-500">Active Users</p>
            </div>
          </div>
        </div>

        {/* Users table */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="font-semibold text-gray-900">All Users</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Joined</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">AWS Accounts</th>
                  <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Role</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {data?.users.map(user => (
                  <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 font-medium text-gray-900">{user.email}</td>
                    <td className="px-6 py-4 text-gray-500">
                      {new Date(user.created_at).toLocaleDateString('en-US', {
                        year: 'numeric', month: 'short', day: 'numeric'
                      })}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        user.aws_accounts_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {user.aws_accounts_count} connected
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.is_admin ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                          <Shield className="w-3 h-3" /> Admin
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">User</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        </div>}

        {/* News tab */}
        {activeTab === 'news' && <div className="space-y-6">

        {/* Community Discussions Feed */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-800" />
              <h2 className="font-semibold text-gray-900">Community Discussions</h2>
              <span className="text-xs text-gray-400">cloud cost · finops · aws</span>
            </div>
            <button
              onClick={fetchFeeds}
              disabled={feedsLoading}
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-emerald-800 transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${feedsLoading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>

          {/* Source filter tabs */}
          <div className="px-6 py-3 border-b border-gray-100 flex gap-2 flex-wrap">
            {(Object.keys(SOURCE_META) as FeedSource[]).map(src => (
              <button
                key={src}
                onClick={() => setActiveSource(src)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  activeSource === src
                    ? SOURCE_META[src].color + ' ring-1 ring-current'
                    : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                }`}
              >
                {SOURCE_META[src].label}
                {src !== 'all' && (
                  <span className="ml-1 opacity-60">
                    {feeds.filter(f => f.source === src).length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Feed list */}
          {feedsLoading ? (
            <div className="divide-y divide-gray-50">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="px-6 py-4 animate-pulse">
                  <div className="h-4 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/4" />
                </div>
              ))}
            </div>
          ) : filteredFeeds.length === 0 ? (
            <p className="px-6 py-10 text-center text-gray-400 text-sm">No discussions found.</p>
          ) : (
            <div className="divide-y divide-gray-50">
              {filteredFeeds.map(item => (
                <a
                  key={item.id}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-4 flex items-start justify-between gap-4 hover:bg-gray-50 transition-colors group"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${SOURCE_META[item.source].color}`}>
                        {SOURCE_META[item.source].label}
                      </span>
                      <span className="text-xs text-gray-400">{timeAgo(item.publishedAt)}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 group-hover:text-emerald-800 transition-colors line-clamp-2">
                      {item.title}
                    </p>
                    <div className="flex items-center gap-3 mt-1.5 text-xs text-gray-400">
                      <span>{item.author}</span>
                      {item.points !== undefined && (
                        <span className="flex items-center gap-0.5">
                          <TrendingUp className="w-3 h-3" /> {item.points}
                        </span>
                      )}
                      {item.comments !== undefined && (
                        <span className="flex items-center gap-0.5">
                          <MessageSquare className="w-3 h-3" /> {item.comments}
                        </span>
                      )}
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-blue-500 flex-shrink-0 mt-0.5 transition-colors" />
                </a>
              ))}
            </div>
          )}
        </div>
        </div>}

        {/* Blog tab */}
        {activeTab === 'blog' && <BlogEditor />}

      </div>
    </div>
  );
}
